import { Component, OnInit, inject } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { RouterLink } from '@angular/router';

import { AuthenticationService } from '@core/auth/authentication.service';
import { Card } from '@shared/components/common/card/card';
import { PageHeaderComponent } from '@shared/components/common/page-header/page-header.component';
import { Badge, BadgeColor } from '@shared/components/ui/badge/badge';
import { Spinner } from '@shared/components/ui/spinner/spinner';
import { ErrorService } from '@shared/services/error.service';
import { parsearFechaLocal, sumarDiasLocal } from '@shared/utils/fecha-local';
import { AgendaItem, AlertasInicio, Inicio, PendientesLaboratorio } from './inicio.interface';
import { InicioService } from './inicio.service';

interface DiaAgenda {
  fecha: string;
  etiqueta: string;
  items: AgendaItem[];
}

/** Mismos colores que el badge de estado del calendario (`EstadoAgendamiento::badgeColor`). */
const COLORES_ESTADO: Record<string, BadgeColor> = {
  P: 'warning',
  G: 'primary',
  R: 'info',
  F: 'success',
};

@Component({
  selector: 'app-home',
  imports: [Badge, Card, MatIcon, PageHeaderComponent, RouterLink, Spinner],
  templateUrl: './home.html',
  styleUrl: './home.scss'
})
export class Home implements OnInit {
  loading = true;
  hoy = '';
  agenda: DiaAgenda[] | null = null;
  pendientes: PendientesLaboratorio | null = null;
  alertas: AlertasInicio | null = null;

  private inicioService = inject(InicioService);
  private errorService = inject(ErrorService);
  private auth = inject(AuthenticationService);

  /** Un usuario de laboratorio sin el módulo de agendamientos abre el OPU desde el PIVE. */
  private readonly verAgendamientos = this.auth.canAccessModule('agendamientos');

  get cantidadAlertas(): number {
    if (!this.alertas) return 0;

    return this.alertas.ventanas_fiv.length + this.alertas.cupo_opu.length + this.alertas.contratos.length;
  }

  get cantidadPendientes(): number {
    if (!this.pendientes) return 0;

    return this.pendientes.recepciones.length + this.pendientes.fiv.length
      + this.pendientes.cultivos.length + this.pendientes.diagnosticos.length;
  }

  ngOnInit(): void {
    this.inicioService.index().subscribe({
      next: (response) => this.cargar(response.data),
      error: (error) => {
        this.loading = false;
        this.errorService.handleError(error);
      },
    });
  }

  rutaAgenda(item: AgendaItem): (string | number)[] {
    if (this.verAgendamientos) {
      return ['/agendamientos', item.tipo === 'OPU' ? 'opu' : 'transferencias', item.id];
    }

    // return ['/laboratorio', item.tipo === 'OPU' ? 'agendamientos' : 'transferencias', item.id];
    return [item.tipo === 'OPU' ? '/pive' : '/te', item.id];
  }

  colorEstado(codigo: string): BadgeColor {
    return COLORES_ESTADO[codigo] ?? 'light';
  }

  /** `yyyy-mm-dd` → `dd/mm`. */
  diaMes(iso: string): string {
    const [, mes, dia] = iso.slice(0, 10).split('-');

    return `${dia}/${mes}`;
  }

  /** `yyyy-mm-dd HH:mm` → `dd/mm HH:mm`. */
  diaMesHora(valor: string): string {
    return `${this.diaMes(valor)} ${valor.slice(11, 16)}`;
  }

  private cargar(data: Inicio): void {
    this.hoy = data.hoy;
    this.agenda = data.agenda ? this.agruparPorDia(data.agenda) : null;
    this.pendientes = data.pendientes;
    this.alertas = data.alertas;
    this.loading = false;
  }

  /**
   * Una TE de varios días se lista en el día en que empieza, o hoy si ya
   * empezó: así no se repite en cada día del rango.
   */
  private agruparPorDia(items: AgendaItem[]): DiaAgenda[] {
    const dias = new Map<string, AgendaItem[]>();

    for (const item of items) {
      const fecha = item.fecha < this.hoy ? this.hoy : item.fecha;
      dias.set(fecha, [...(dias.get(fecha) ?? []), item]);
    }

    return [...dias.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([fecha, itemsDelDia]) => ({ fecha, etiqueta: this.etiquetaDia(fecha), items: itemsDelDia }));
  }

  private etiquetaDia(fecha: string): string {
    if (fecha === this.hoy) return 'Hoy';
    if (fecha === sumarDiasLocal(this.hoy, 1)) return 'Mañana';

    const texto = parsearFechaLocal(fecha)!.toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'numeric' });

    return texto.charAt(0).toUpperCase() + texto.slice(1);
  }
}
